import { useMatomo } from '@datapunt/matomo-tracker-react';
import cx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { ReactComponent as CollapseIcon } from '@assets/svg/ic-collapse.svg';

import Header from '@components/common/Header/Header';
import CopyLinkIconBtn from '@components/common/Header/components/BtnHeaderCopyLink/BtnHeaderCopyLink';
import PrintIconBtn from '@components/common/Header/components/BtnHeaderPrint/BtnHeaderPrint';
import SkeletonBlock from '@components/common/Skeleton/SkeletonBlock';
import { useEquipmentImagesListQuery } from '@components/modals/EditSensorGalleryModal/services/useEquipmentImagesListQuery';
import Button from '@components/uikit/Button/Button';
import Switch from '@components/uikit/Switch/Switch';

import useTranslation from '@hooks/useTranslation';

import type { ILocationStructureNode } from '@model/structureData';

import { selectSensorWithAi, useSensorStoreActions } from '@pages/Sensors/store/useSensorStore';

import { useAssetTreeQuery } from '@services/useAssetTreeQuery';

import AssetCodeInput from './components/AssetCodeInput/AssetCodeInput';
import NotesSection from './components/NotesSection/NotesSection';
import SensorGallery from './components/SensorGallery/SensorGallery';
import SensorListExtraInfo from './components/SensorListExtraInfo/SensorListExtraInfo';
import SensorListTableBody from './components/SensorListTableBody/SensorListTableBody';
import SensorListTableBodySkeleton from './components/SensorListTableBody/SensorListTableBodySkeleton';
import SensorListTableHead from './components/SensorListTableHead/SensorListTableHead';
import { useEquipmentsViewQuery } from './services/useEquipmentsViewQuery';
import style from './style.module.scss';

const SensorsList = () => {
  const { t } = useTranslation();

  const { trackPageView } = useMatomo();
  const { equipmentId } = useParams();
  const { state } = useLocation();

  const [visibleNotesSection, setVisibleNotesSection] = useState(state?.showNotesBlock || false);
  const printRef = useRef<HTMLInputElement>(null);

  const { setSensorWithAi } = useSensorStoreActions();

  const sensorWithAi = selectSensorWithAi();

  const { data: equipmentObject, isFetching } = useEquipmentsViewQuery({ equipmentId });
  const { data: assetTreeData } = useAssetTreeQuery();

  const equipment = equipmentObject?.equipment;
  const location: ILocationStructureNode | undefined = assetTreeData?.locations?.find(
    (location) => equipment?.location_id && +location.id === +equipment?.location_id,
  );

  useEffect(() => {
    trackPageView({});
  }, []);

  return (
    <div>
      <Header
        title={t('Sensor List')}
        isSecondaryPage={true}
        headerRightComponent={
          <>
            <CopyLinkIconBtn />
            <PrintIconBtn printRef={printRef} />
          </>
        }
        extraInfo={<SensorListExtraInfo info={location?.name || location?.path || ''} />}
      />
      <div className={style.sensorsListBody} ref={printRef}>
        <div className={style.bodyExtraPanel}>
          <div className={style.bodyExtraPanelInfo}>
            <div className={style.bodyExtraPanelLeft}>
              <div className={style.bodyExtraPanelTitle}>{t('Equipment name')}</div>
              {isFetching ? (
                <SkeletonBlock className={style.equipmentNameSkeleton} />
              ) : (
                <div className={style.bodyExtraPanelSubtitle}>
                  <div className={style.nameBlock}>
                    <span className={style.pointName}>{equipment?.name}</span>
                    <Button
                      type="secondary"
                      text={t('View notes')}
                      rightElement={
                        <CollapseIcon
                          className={cx(style.collapseIcon, {
                            [style.hideCollapseIcon]: visibleNotesSection,
                          })}
                        />
                      }
                      onClick={() => setVisibleNotesSection(!visibleNotesSection)}
                    />
                  </div>
                  <Switch
                    value={sensorWithAi}
                    onChange={() => setSensorWithAi(!sensorWithAi)}
                    label={t('Only Sensor with Action Item')}
                    type={'woHelperText'}
                  />
                </div>
              )}
            </div>
            <div className={style.bodyExtraPanelRight}>
              <AssetCodeInput
                equipment={equipment}
                locationId={location?.id || assetTreeData?.customerLevelId || ''}
              />
            </div>
          </div>
          <SensorGallery
            installationPoints={equipmentObject?.equipment.installationPoints}
            equipmentName={equipmentObject?.equipment.name}
            isInstallationPointsFetching={isFetching}
          />
        </div>
        {visibleNotesSection && (
          <NotesSection
            equipmentId={equipmentId}
            equipmentName={equipment?.name}
            description={equipment?.description}
            links={equipment?.links}
            attachments={equipment?.attachments}
          />
        )}
        <div className={style.bodyMainPanel}>
          <SensorListTableHead />
          {isFetching ? <SensorListTableBodySkeleton /> : <SensorListTableBody equipment={equipment} />}
        </div>
      </div>
    </div>
  );
};

export default SensorsList;
